import { formatAmount } from "@babia/checks";
import { TYPE_EMOJI, WAKE_EMOJI } from "../../lib/entry-text.ts";
import type { LogStats } from "../../lib/stats.ts";
import { cn } from "../../lib/utils.ts";
import { Button } from "../ui/button.tsx";

/** The emoji beside a button label; the words carry the meaning. */
function Emoji({ children, className }: { children: string; className?: string }) {
  return (
    <span aria-hidden="true" className={cn("text-[20px] leading-none", className)}>
      {children}
    </span>
  );
}

/**
 * Up to five actions: Change and Sleep only when the family tracks them. Each
 * tile carries its emoji and, as a left rule, the colour of its dot in the
 * timeline, so a button and the rows it makes read as one thing. The sleep
 * tile names the event a tap records ("Fell asleep", "Woke up") and shows the
 * state right now beneath it; while a baby sleeps it turns the sleep colour.
 * An odd tile out spans the row, so the grid never has a gap.
 */
export function QuickActions({
  stats,
  busy,
  bottleReady,
  sleep,
  diaper,
  onBottle,
  onChange,
  onSleepToggle,
  onMeds,
  onNote,
}: {
  stats: LogStats;
  /** True while an open sleep is still a temporary row (can't be ended yet). */
  busy: boolean;
  /** The household's bottle unit is known, so a bottle can be saved in it. */
  bottleReady: boolean;
  /** Show the Sleep tile. */
  sleep: boolean;
  /** Show the Change tile. */
  diaper: boolean;
  onBottle: () => void;
  onChange: () => void;
  onSleepToggle: () => void;
  onMeds: () => void;
  onNote: () => void;
}) {
  const asleep = !!stats.openSleep;
  const tiles: (TileProps & { key: string })[] = [
    ...(diaper
      ? [
          {
            key: "change",
            emoji: TYPE_EMOJI.diaper,
            label: "Change",
            sub: stats.diaperSub,
            rule: "border-l-accent-2",
            onClick: onChange,
          },
        ]
      : []),
    ...(sleep
      ? [
          {
            key: "sleep",
            emoji: asleep ? WAKE_EMOJI : TYPE_EMOJI.sleep,
            label: stats.sleepLabel,
            sub: stats.sleepSub,
            rule: "border-l-chart-sleep",
            onClick: onSleepToggle,
            disabled: busy,
            className: cn(asleep && "is-sleeping"),
          },
        ]
      : []),
    {
      key: "meds",
      emoji: TYPE_EMOJI.meds,
      label: "Meds",
      sub: stats.medsSub,
      rule: "border-l-neutral-600",
      onClick: onMeds,
    },
    {
      key: "note",
      emoji: TYPE_EMOJI.note,
      label: "Note",
      sub: "Anything else",
      rule: "border-l-neutral-400",
      onClick: onNote,
    },
  ];
  return (
    <section className="mt-6 grid grid-cols-2 gap-2.5" aria-label="Log">
      <Button
        variant="primary"
        onClick={onBottle}
        disabled={!bottleReady}
        className="col-span-2 min-h-[76px] justify-start gap-3 px-[18px] text-left"
      >
        <Emoji className="text-[28px]">{TYPE_EMOJI.feed}</Emoji>
        <span className="heading text-[26px] tracking-[-0.02em]">Log a bottle</span>
        <span className="ml-auto text-[13px] font-normal opacity-85">
          {formatAmount(stats.usualOz, stats.unit)} usual
        </span>
      </Button>

      {tiles.map(({ key, className, ...tile }, i) => (
        <Tile
          key={key}
          {...tile}
          className={cn(
            className,
            tiles.length % 2 === 1 && i === tiles.length - 1 && "col-span-2",
          )}
        />
      ))}
    </section>
  );
}

interface TileProps {
  emoji: string;
  label: string;
  sub: string;
  /** The type's timeline-dot colour as a left rule. */
  rule: string;
  className?: string;
  onClick: () => void;
  disabled?: boolean;
}

function Tile({ emoji, label, sub, rule, className, ...props }: TileProps) {
  return (
    <Button variant="tile" className={cn("border-l-4", rule, className)} {...props}>
      <span className="flex items-center gap-2">
        <Emoji>{emoji}</Emoji>
        <span className="heading text-[19px]">{label}</span>
      </span>
      <span className="text-[12px] opacity-65">{sub}</span>
    </Button>
  );
}
